import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Music, Search, Users } from "lucide-react";
import { api } from "@/lib/api";

const Artists = () => {
  const [q, setQ] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/profile/directory", { params: { limit: 120, verified_only: verifiedOnly } })
      .then((r) => { if (mounted) setItems(r.data?.results || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [verifiedOnly]);

  const filtered = q.trim()
    ? items.filter((a) => a.display_name.toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  return (
    <div data-testid="artists-page" className="px-4 sm:px-8 py-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#E2FF31] font-bold mb-2 flex items-center gap-1.5">
          <Users size={12} /> Artist Directory
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-2">
          Meet the artists.
        </h1>
        <p className="text-white/60">
          Every Premium artist on 1o1sound. Verified Artists carry a yellow badge.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="artists-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by artist name…"
            className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-white/30 transition"
          />
        </div>
        <button
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="artists-verified-toggle"
          className={`px-5 py-3 rounded-full text-sm font-semibold transition flex items-center gap-2 ${
            verifiedOnly
              ? "bg-[#E2FF31] text-black"
              : "bg-white/5 hover:bg-white/10 text-white/70"
          }`}
          type="button"
        >
          <BadgeCheck size={14} />
          {verifiedOnly ? "Verified only ✓" : "Show verified only"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-white/50 py-12 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 px-4 rounded-2xl bg-white/[0.03] border border-white/8">
          <Music size={36} className="mx-auto text-white/30 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No artists yet</h3>
          <p className="text-white/55 text-sm max-w-md mx-auto">
            {q.trim()
              ? "No artists match your filter."
              : "Be the first Premium artist on the platform — your name shows up here the moment you sign up."}
          </p>
        </div>
      ) : (
        <div
          data-testid="artists-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {filtered.map((a) => (
            <Link
              key={a.user_id}
              to={`/profile/${a.user_id}`}
              data-testid={`artist-card-${a.user_id}`}
              className="group rounded-2xl bg-white/[0.03] border border-white/8 hover:border-white/20 p-4 transition text-center"
            >
              <div className="relative mx-auto mb-3 w-24 h-24">
                {a.picture ? (
                  <img
                    src={a.picture}
                    alt=""
                    className="w-full h-full rounded-full object-cover shadow-lg"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center shadow-lg">
                    <span className="font-display text-3xl font-bold text-white">
                      {(a.display_name[0] || "?").toUpperCase()}
                    </span>
                  </div>
                )}
                {a.verified && (
                  <span
                    data-testid={`artist-verified-${a.user_id}`}
                    className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-black border-2 border-[#E2FF31] flex items-center justify-center"
                  >
                    <BadgeCheck size={14} className="text-[#E2FF31]" fill="currentColor" stroke="black" />
                  </span>
                )}
              </div>
              <p className="font-semibold text-sm truncate group-hover:text-white text-white/90">
                {a.display_name}
              </p>
              <p className="text-xs text-white/45 mt-0.5">
                {a.uploads} track{a.uploads === 1 ? "" : "s"}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Artists;
